import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { CheckCircle, ContentCopy, Email } from "@mui/icons-material";
import { enqueueSnackbar } from "notistack";
import { useApiClient } from "../../../api";
import { checkHasApprovedSubscription } from "../../auth/Utils";
import Tooltip from "@mui/material/Tooltip";

export function VerificationAndReferralStatus() {
  const ApiClient = useApiClient();
  const [referralStatus, setReferralStatus] = useState({});

  const hasApprovedSubscription = checkHasApprovedSubscription();
  const [dpoEmail, setDpoEmail] = useState("");

  const handleVerificationRequest = async () => {
    try {
      const result = await ApiClient.users.requestGetSchoolApproval({
        dpoEmail: dpoEmail || null,
      });

      if (result.success) {
        enqueueSnackbar("School verification request sent", {
          variant: "success",
        });
      } else {
        enqueueSnackbar(result.message || "Unable to request verification", {
          variant: "error",
        });
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Unable to request school verification", {
        variant: "error",
      });
    }
  };
  const fetchUserReferralData = async () => {
    try {
      const data = await ApiClient.users.fetchUserReferralData();
      setReferralStatus(data);
      console.log("Referral status:", data);
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Unable to get referral data. Please try again later.", {
        variant: "error",
      });
    }
  };

  useEffect(() => {
    fetchUserReferralData();
  }, []);

  const copyText = async (value) => {
    await navigator.clipboard.writeText(value);

    enqueueSnackbar("Copied to clipboard", {
      variant: "success",
    });
  };

  return (
    <>
      <Card sx={{ mt: 2 }} data-testid="school-verification-card">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            School verification
          </Typography>

          <Stack spacing={3}>
            <Box>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography variant="subtitle1">School verification</Typography>

                {hasApprovedSubscription ? (
                  <Chip
                    color="success"
                    icon={<CheckCircle />}
                    label="Verified"
                  />
                ) : (
                  <Chip label="Not verified" />
                )}
              </Stack>

              {!hasApprovedSubscription && (
                <>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Verify your school to enable managed onboarding, class
                    syncing and school-wide setup. This remains free and may
                    require approval from your school's DPO or leadership team.
                  </Typography>

                  <TextField
                    fullWidth
                    label="DPO email (optional)"
                    value={dpoEmail}
                    onChange={(e) => setDpoEmail(e.target.value)}
                    helperText="If known, we can contact your DPO directly to speed up the verification process."
                    sx={{ mb: 2 }}
                  />

                  <Button
                    variant="contained"
                    onClick={handleVerificationRequest}
                  >
                    Request school verification
                  </Button>
                </>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>
      <Card sx={{ mt: 2 }} data-testid="referral-card">
        <CardContent>
          <Stack spacing={3}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Referrals
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Invite colleagues and track successful referrals.
              </Typography>

              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems="stretch"
              >
                <Stack spacing={2} sx={{ flex: 1 }}>
                  <TextField
                    label="Referral code"
                    value={referralStatus.code}
                    disabled
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end" sx={{ mr: 1 }}>
                          <IconButton
                            edge="end"
                            onClick={() => copyText(referralStatus.code)}
                            sx={{ border: 0 }}
                          >
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    label="Referral link"
                    value={referralStatus.link}
                    disabled
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end" sx={{ mr: 1 }}>
                          <Tooltip title="Email referral link">
                            <IconButton
                              data-testid={"copy-referral-link-button"}
                              edge="end"
                              onClick={() => {
                                const subject = encodeURIComponent(
                                  "Join me on Fruitify",
                                );
                                const body = encodeURIComponent(
                                  `Hi,\n\nI'd like to invite you to try Fruitify. You can sign up here:\n\n${referralStatus.link}\n\n`,
                                );

                                window.open(
                                  `mailto:?subject=${subject}&body=${body}`,
                                );
                              }}
                              sx={{ border: 0 }}
                            >
                              <Email fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Copy referral link">
                            <IconButton
                              edge="end"
                              onClick={() => copyText(referralStatus.link)}
                              sx={{ border: 0 }}
                            >
                              <ContentCopy fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Stack>

                <Box
                  sx={{
                    width: { md: "40%" },
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 2,
                    alignContent: "start",
                  }}
                >
                  <Box sx={{ gridColumn: "1 / -1" }}>
                    <Typography variant="body2" color="text.secondary">
                      Total referrals
                    </Typography>

                    <Typography variant="h4">
                      {referralStatus.totalReferrals}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Other schools
                    </Typography>

                    <Typography variant="h5">
                      {referralStatus.otherSchoolReferrals}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Your school
                    </Typography>

                    <Typography variant="h5">
                      {referralStatus.sameSchoolReferrals}
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </>
  );
}
